import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock3, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import EnhancedSearch from './EnhancedSearch';
import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { getContactInitialsClassName } from '../utils/colorUtils';
import '../styles/TimeBilling.css';

const MAX_HOURS = 1000;
const MAX_RATE = 99999999.99;

export const normalizeTimeBillingCase = (caseValue) => {
    const caseId = Number(caseValue?.value ?? caseValue?.id ?? caseValue?.case_id);
    if (!Number.isInteger(caseId) || caseId <= 0) return null;
    return {
        ...caseValue,
        id: caseId,
        value: String(caseId),
        type: 'case',
        label: caseValue?.label || caseValue?.case_name || caseValue?.name || `Case #${caseId}`,
    };
};

export const getTimeBillingCaseInitials = (caseValue) => {
    const displayName = String(
        caseValue?.contactName
        || caseValue?.contact_name
        || caseValue?.label
        || caseValue?.case_name
        || caseValue?.name
        || 'DB'
    ).trim();
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'DB';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

export const formatTimeBillingLocalDate = (now = new Date()) => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const createTimeBillingRequestId = (
    cryptoSource = typeof window !== 'undefined' ? window.crypto : undefined
) => {
    if (typeof cryptoSource?.randomUUID === 'function') {
        return cryptoSource.randomUUID();
    }
    if (typeof cryptoSource?.getRandomValues !== 'function') {
        throw new Error('Secure request IDs are not available in this browser.');
    }

    const bytes = cryptoSource.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const getTimeBillingDraftErrors = (draft = {}, { requireManualRate = false } = {}) => {
    const errors = {};
    const caseId = Number(draft.case_id);
    const hours = Number(draft.hours);
    const description = String(draft.description || '').trim();
    const dateMatch = String(draft.date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!Number.isInteger(caseId) || caseId <= 0) errors.case_id = 'Select a case.';
    if (!dateMatch) {
        errors.date = 'Enter a valid date.';
    } else {
        const date = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
        if (
            date.getFullYear() !== Number(dateMatch[1])
            || date.getMonth() !== Number(dateMatch[2]) - 1
            || date.getDate() !== Number(dateMatch[3])
        ) errors.date = 'Enter a valid date.';
    }
    if (!Number.isFinite(hours) || hours <= 0 || hours > MAX_HOURS) {
        errors.hours = `Enter time greater than zero and no more than ${MAX_HOURS} hours.`;
    }
    if (!description) errors.description = 'Enter a description.';
    if (requireManualRate) {
        const rawRate = String(draft.manual_rate ?? '').trim();
        const rate = Number(rawRate);
        if (
            rawRate === ''
            || !/^\d+(?:\.\d{1,2})?$/.test(rawRate)
            || !Number.isFinite(rate)
            || rate <= 0
            || rate > MAX_RATE
        ) {
            errors.manual_rate = `Enter a rate greater than zero and no more than ${MAX_RATE.toLocaleString('en-US')} per hour.`;
        }
    }
    return errors;
};

const endpointFor = (apiBase) => {
    const normalizedBase = String(apiBase || '').trim().replace(/\/+$/, '');
    return normalizedBase ? `${normalizedBase}/my-time-billing.php` : caseDbApiEndpoint('my-time-billing.php');
};

const currency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';
    return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const readJsonResponse = async (response, fallback) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success !== true) {
        const error = new Error(payload?.message || `${fallback} (${response.status}).`);
        error.reasonCode = payload?.reason_code || '';
        throw error;
    }
    return payload;
};

const TimeBillingEntryModal = ({
    onClose,
    onCreated = () => {},
    initialCase = null,
    apiBase = '',
    requestIdFactory = createTimeBillingRequestId,
}) => {
    const navigate = useNavigate();
    const endpoint = endpointFor(apiBase);
    const normalizedInitialCase = normalizeTimeBillingCase(initialCase);
    const [selectedCase, setSelectedCase] = useState(normalizedInitialCase);
    const [context, setContext] = useState(null);
    const [contextLoading, setContextLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [contextRevision, setContextRevision] = useState(0);
    const [draft, setDraft] = useState(() => ({
        case_id: normalizedInitialCase ? Number(normalizedInitialCase.value) : null,
        date: formatTimeBillingLocalDate(),
        hours: '',
        description: '',
        manual_rate: '',
        request_id: requestIdFactory(),
    }));
    const contextGenerationRef = useRef(0);
    const caseClearButtonRef = useRef(null);
    const focusSelectedCaseRef = useRef(false);

    useEffect(() => {
        if (!selectedCase || !focusSelectedCaseRef.current) return;
        focusSelectedCaseRef.current = false;
        caseClearButtonRef.current?.focus();
    }, [selectedCase]);

    useEffect(() => {
        const caseId = Number(draft.case_id);
        const generation = ++contextGenerationRef.current;
        setContext(null);
        if (!Number.isInteger(caseId) || caseId <= 0) {
            setContextLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        setContextLoading(true);
        const params = new URLSearchParams({ action: 'case_context', case_id: String(caseId) });
        fetch(`${endpoint}?${params.toString()}`, {
            credentials: 'include',
            signal: controller.signal,
        })
            .then(response => readJsonResponse(response, 'Billing details could not be loaded'))
            .then(payload => {
                if (generation !== contextGenerationRef.current) return;
                setContext(payload);
                setError('');
                if (payload.manual_rate_allowed !== true) {
                    setDraft(previous => previous.case_id === caseId
                        ? { ...previous, manual_rate: '' }
                        : previous);
                }
                if (payload.case?.name) {
                    setSelectedCase(previous => ({
                        ...previous,
                        id: caseId,
                        value: String(caseId),
                        type: 'case',
                        label: payload.case.name || previous?.label || `Case #${caseId}`,
                    }));
                }
            })
            .catch(fetchError => {
                if (fetchError?.name !== 'AbortError' && generation === contextGenerationRef.current) {
                    setError(fetchError.message || 'Billing details could not be loaded.');
                }
            })
            .finally(() => {
                if (generation === contextGenerationRef.current) setContextLoading(false);
            });

        return () => controller.abort();
    }, [contextRevision, draft.case_id, endpoint]);

    const changeCase = (option) => {
        if (saving) return;
        const normalizedCase = normalizeTimeBillingCase(option);
        const validCaseId = normalizedCase?.id ?? null;
        if (validCaseId !== null && validCaseId === Number(draft.case_id)) {
            setSelectedCase(previous => ({ ...previous, ...normalizedCase }));
            setFieldErrors(previous => ({ ...previous, case_id: undefined }));
            return;
        }
        focusSelectedCaseRef.current = validCaseId !== null;
        contextGenerationRef.current += 1;
        setContext(null);
        setContextLoading(validCaseId !== null);
        setError('');
        setSelectedCase(normalizedCase);
        setDraft(previous => ({
            ...previous,
            case_id: validCaseId,
            manual_rate: '',
            request_id: requestIdFactory(),
        }));
        setFieldErrors(previous => ({ ...previous, case_id: undefined, manual_rate: undefined }));
    };

    const submit = async (event) => {
        event?.preventDefault?.();
        if (saving || contextLoading) return;
        const manualRateAllowed = context?.manual_rate_allowed === true && context?.rate == null;
        const errors = getTimeBillingDraftErrors(draft, { requireManualRate: manualRateAllowed });
        setFieldErrors(errors);
        setError('');
        if (Object.keys(errors).length > 0) return;
        if (!context || (!context?.rate?.id && !manualRateAllowed)) {
            setError('Billing details are not available for the selected case.');
            return;
        }

        setSaving(true);
        try {
            const body = {
                case_id: Number(draft.case_id),
                date: draft.date,
                hours: draft.hours,
                description: String(draft.description || '').trim(),
                request_id: draft.request_id,
                confirmed: true,
            };
            if (manualRateAllowed) body.manual_rate = String(draft.manual_rate).trim();

            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const payload = await readJsonResponse(response, 'The time entry could not be added');
            setSuccess(payload);
            onCreated(payload);
        } catch (saveError) {
            setError(saveError.message || 'The time entry could not be added.');
            if (['manual_rate_required', 'manual_rate_not_allowed'].includes(saveError.reasonCode)) {
                contextGenerationRef.current += 1;
                setContext(null);
                setFieldErrors(previous => ({ ...previous, manual_rate: undefined }));
                setContextRevision(previous => previous + 1);
            }
        } finally {
            setSaving(false);
        }
    };

    if (success) {
        const entry = success.entry || {};
        return (
            <Modal
                title="Successfully added"
                single
                onClose={onClose}
                footer={(
                    <div className="modal-footer-actions">
                        <button type="button" className="action alt" onClick={onClose}>Close</button>
                        <button
                            type="button"
                            className="action"
                            onClick={() => {
                                onClose?.();
                                navigate('/time-billing');
                            }}
                        >
                            View Time &amp; Billing
                        </button>
                    </div>
                )}
            >
                <div className="modal-content-wrapper">
                    <div className="my-time-success" role="status">
                        <CheckCircle2 size={42} aria-hidden="true" />
                        <div>
                            <h3>Time entry saved</h3>
                            <p>
                                {entry.case_name || selectedCase?.label || 'Selected case'}
                                {' · '}{entry.hours ?? success.rounded_hours ?? draft.hours} hours
                                {entry.total !== undefined ? ` · ${currency(entry.total)}` : ''}
                            </p>
                            {success.already_created === true && <p>This request had already been saved; no duplicate was created.</p>}
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }

    const increment = context?.schedule?.increment_hours;
    const manualRateAllowed = context?.manual_rate_allowed === true && context?.rate == null;
    const hasUsableRateContext = Boolean(context?.rate?.id) || manualRateAllowed;

    return (
        <Modal
            title="Add Time"
            single
            onClose={() => !saving && onClose?.()}
            footer={(
                <div className="modal-footer-actions">
                    <button type="button" className="action alt" onClick={onClose} disabled={saving}>Cancel</button>
                    <button
                        type="submit"
                        form="global-time-entry-form"
                        className="action"
                        disabled={saving || contextLoading || !hasUsableRateContext}
                        aria-busy={saving}
                    >
                        {saving ? 'Adding…' : 'Add Time'}
                    </button>
                </div>
            )}
        >
            <div className="modal-content-wrapper">
                <form id="global-time-entry-form" className="my-time-entry-form" onSubmit={submit} noValidate>
                    {error && <div className="my-time-error" role="alert">{error}</div>}
                    <div className="form-group nm">
                        <label className="subtext">Case*</label>
                        {selectedCase ? (
                            <div
                                className="mini-case-summary my-time-case-summary"
                                role="group"
                                aria-label="Selected case"
                            >
                                <span
                                    className={`${getContactInitialsClassName('mini-case-initials', selectedCase.id)} my-time-case-initials`}
                                    aria-hidden="true"
                                >
                                    {getTimeBillingCaseInitials(selectedCase)}
                                </span>
                                <div className="mini-case-meta">
                                    <div className="mini-case-name">
                                        <span className="mini-case-name-text">{selectedCase.label}</span>
                                        <span className="mini-case-id subtext" style={{ lineHeight: '16px' }}>({selectedCase.id})</span>
                                    </div>
                                    <div className="mini-case-info subtext">
                                        {(selectedCase.contactName || selectedCase.contact_name) && (
                                            <span>{selectedCase.contactName || selectedCase.contact_name}</span>
                                        )}
                                        {(selectedCase.phase || selectedCase.status) && (
                                            <span>{selectedCase.phase || selectedCase.status}</span>
                                        )}
                                        {(selectedCase.assignedUser || selectedCase.assigned_user) && (
                                            <span>{selectedCase.assignedUser || selectedCase.assigned_user}</span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    ref={caseClearButtonRef}
                                    type="button"
                                    className="my-time-case-clear"
                                    onClick={() => changeCase(null)}
                                    disabled={saving}
                                    aria-label={`Remove ${selectedCase.label}`}
                                    title="Choose a different case"
                                >
                                    <X size={18} aria-hidden="true" />
                                </button>
                            </div>
                        ) : (
                            <div className="my-time-case-search">
                                <EnhancedSearch
                                    expanded
                                    setExpanded={() => {}}
                                    modal
                                    initiallyActive
                                    selectionMode
                                    caseOnly
                                    onChange={changeCase}
                                />
                            </div>
                        )}
                        {fieldErrors.case_id && <span className="my-time-field-error">{fieldErrors.case_id}</span>}
                    </div>

                    <div className="my-time-context" aria-live="polite">
                        {contextLoading ? (
                            <span>Loading your rate…</span>
                        ) : context ? (
                            <>
                                <span><strong>{context.actor?.name || 'Your account'}</strong></span>
                                <span>
                                    {context.schedule === null
                                        ? 'No billing rate schedule set'
                                        : (context.schedule?.name || 'Billing schedule')}
                                </span>
                                {context.rate ? (
                                    <span>{currency(context.rate.rate)} / hour</span>
                                ) : manualRateAllowed ? (
                                    <span>No assigned rate; enter one below for this entry</span>
                                ) : (
                                    <span>Rate unavailable</span>
                                )}
                                {Number(increment) > 0 && <span>Rounds up to {Number(increment)} hour increments</span>}
                            </>
                        ) : (
                            <span>Select a case to load your assigned rate.</span>
                        )}
                    </div>

                    {manualRateAllowed && (
                        <div className="form-group nm">
                            <label className="subtext" htmlFor="global-time-entry-manual-rate">Hourly Rate*</label>
                            <div className="number-input">
                                <input
                                    id="global-time-entry-manual-rate"
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0.01"
                                    max={MAX_RATE}
                                    placeholder="0.00"
                                    value={draft.manual_rate}
                                    onChange={event => {
                                        setDraft(previous => ({ ...previous, manual_rate: event.target.value }));
                                        setFieldErrors(previous => ({ ...previous, manual_rate: undefined }));
                                    }}
                                    aria-describedby={fieldErrors.manual_rate ? 'global-time-entry-manual-rate-error' : undefined}
                                    aria-invalid={Boolean(fieldErrors.manual_rate)}
                                    aria-required="true"
                                />
                                <span className="number-symbol subtext">$</span>
                            </div>
                            {fieldErrors.manual_rate && (
                                <span id="global-time-entry-manual-rate-error" className="my-time-field-error">
                                    {fieldErrors.manual_rate}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="my-time-entry-grid">
                        <div className="form-group nm">
                            <label className="subtext" htmlFor="global-time-entry-date">Date*</label>
                            <input
                                id="global-time-entry-date"
                                type="date"
                                inputMode="numeric"
                                pattern="\d{4}-\d{2}-\d{2}"
                                value={draft.date}
                                onChange={event => {
                                    setDraft(previous => ({ ...previous, date: event.target.value }));
                                    setFieldErrors(previous => ({ ...previous, date: undefined }));
                                }}
                                aria-describedby={fieldErrors.date ? 'global-time-entry-date-error' : undefined}
                                aria-invalid={Boolean(fieldErrors.date)}
                                aria-required="true"
                            />
                            {fieldErrors.date && (
                                <span id="global-time-entry-date-error" className="my-time-field-error">
                                    {fieldErrors.date}
                                </span>
                            )}
                        </div>
                        <div className="form-group nm">
                            <label className="subtext" htmlFor="global-time-entry-hours">Hours*</label>
                            <input
                                id="global-time-entry-hours"
                                type="number"
                                inputMode="decimal"
                                step="any"
                                min="0.000001"
                                max={MAX_HOURS}
                                placeholder="0.00"
                                value={draft.hours}
                                onChange={event => {
                                    setDraft(previous => ({ ...previous, hours: event.target.value }));
                                    setFieldErrors(previous => ({ ...previous, hours: undefined }));
                                }}
                                aria-describedby={fieldErrors.hours ? 'global-time-entry-hours-error' : undefined}
                                aria-invalid={Boolean(fieldErrors.hours)}
                                aria-required="true"
                            />
                            {fieldErrors.hours && (
                                <span id="global-time-entry-hours-error" className="my-time-field-error">
                                    {fieldErrors.hours}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="form-group nm">
                        <label className="subtext" htmlFor="global-time-entry-description">Description*</label>
                        <textarea
                            id="global-time-entry-description"
                            value={draft.description}
                            onChange={event => {
                                setDraft(previous => ({ ...previous, description: event.target.value }));
                                setFieldErrors(previous => ({ ...previous, description: undefined }));
                            }}
                            placeholder="What did you work on?"
                            aria-describedby={fieldErrors.description ? 'global-time-entry-description-error' : undefined}
                            aria-invalid={Boolean(fieldErrors.description)}
                            aria-required="true"
                        />
                        {fieldErrors.description && (
                            <span id="global-time-entry-description-error" className="my-time-field-error">
                                {fieldErrors.description}
                            </span>
                        )}
                    </div>

                    <div className="my-time-confirmation-note">
                        <Clock3 size={16} aria-hidden="true" />
                        {manualRateAllowed
                            ? 'This rate applies only to this time entry.'
                            : 'Your account and rate are resolved by CaseDB when you add this entry.'}
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default TimeBillingEntryModal;
